// Components/InviteCandidatesModal.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './InviteCandidatesModal.css';

const EXAM_API_URL = 'https://api.avtechbmt.cloud/api/exams';

/**
 * Props:
 *  - exam      : { _id, title }
 *  - onClose   : fn
 *  - onSuccess : fn(message)
 */
export default function InviteCandidatesModal({ exam, onClose, onSuccess }) {
  const { token } = useAuth();

  const [rows, setRows] = useState([{ name: '', email: '' }]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null); // { sent: [], failed: [] }

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const addRow = () => setRows((r) => [...r, { name: '', email: '' }]);
  const removeRow = (i) =>
    setRows((r) => (r.length === 1 ? r : r.filter((_, idx) => idx !== i)));

  const updateRow = (i, field, value) =>
    setRows((r) => {
      const next = [...r];
      next[i] = { ...next[i], [field]: value };
      return next;
    });

const handleSend = async () => {
  setError('');
  setResults(null);

  const cleaned = rows
    .map((r) => ({
      name: (r.name || '').trim(),
      email: (r.email || '').trim().toLowerCase(),
    }))
    .filter((r) => r.email);

  if (cleaned.length === 0) {
    setError('Please enter at least one email address.');
    return;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const invalid = cleaned.find((r) => !emailRegex.test(r.email));
  if (invalid) {
    setError(`Invalid email: ${invalid.email}`);
    return;
  }

  const seen = new Set();
  const unique = cleaned.filter((r) => {
    if (seen.has(r.email)) return false;
    seen.add(r.email);
    return true;
  });

  setSending(true);

  const url = `${EXAM_API_URL}/${exam._id}/invite`;
  console.log('[Invite] POST', url);
  console.log('[Invite] Payload', { candidates: unique });

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token || localStorage.getItem('token')}`,
      },
      body: JSON.stringify({ candidates: unique }),
    });

    console.log('[Invite] Response status:', res.status);
    console.log('[Invite] Response OK:', res.ok);

    // Read as text first — some error responses are not JSON
    const rawText = await res.text();
    console.log('[Invite] Raw response:', rawText);

    let data;
    try {
      data = JSON.parse(rawText);
    } catch {
      setError(
        `Server returned non-JSON response (HTTP ${res.status}). Check backend logs. Raw: ${rawText.slice(0, 200)}`
      );
      return;
    }

    if (res.ok && data.success) {
      const sent = (data.results || []).filter((r) => r.status === 'sent');
      const failed = (data.results || []).filter((r) => r.status === 'failed');
      setResults({ sent, failed });

      if (typeof onSuccess === 'function') {
        onSuccess(
          failed.length === 0
            ? `Invitations sent to ${sent.length} candidate${sent.length === 1 ? '' : 's'}.`
            : `${sent.length} sent · ${failed.length} failed.`
        );
      }
    } else {
      setError(data.message || `Request failed with HTTP ${res.status}`);
    }
  } catch (err) {
    console.error('[Invite] Fetch failed:', err);
    setError(
      `Network error: ${err.message}. Check browser console (F12 → Network tab) for details.`
    );
  } finally {
    setSending(false);
  }
};

  const handleCSVUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = String(evt.target.result || '');
      const lines = text.split(/\r?\n/).filter(Boolean);
      const parsed = [];
      lines.forEach((line, idx) => {
        // Skip header row if it looks like "name,email" or "email"
        const parts = line.split(',').map((p) => p.trim());
        if (idx === 0 && /email/i.test(parts[1] || parts[0])) return;

        if (parts.length >= 2) {
          parsed.push({ name: parts[0], email: parts[1] });
        } else if (parts[0]) {
          parsed.push({ name: '', email: parts[0] });
        }
      });
      if (parsed.length) {
        setRows(parsed);
        setError('');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const closeAfterSuccess = () => {
    if (results) {
      // Auto-close so parent can refresh
      onClose();
    } else {
      onClose();
    }
  };

  return (
    <div className="icm-overlay" onClick={closeAfterSuccess}>
      <div className="icm-modal" onClick={(e) => e.stopPropagation()}>
        <header className="icm-header">
          <div>
            <span className="icm-eyebrow">INVITE CANDIDATES</span>
            <h3>{exam.title}</h3>
            <p className="icm-sub">
              Each candidate will receive a unique link by email. Only the
              invited email can take this exam.
            </p>
          </div>
          <button
            className="icm-close"
            onClick={closeAfterSuccess}
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        {error && <div className="icm-alert icm-alert-error">⚠️ {error}</div>}

        {!results && (
          <>
            <div className="icm-toolbar">
              <button
                type="button"
                className="icm-btn icm-btn-secondary"
                onClick={addRow}
              >
                ＋ Add Row
              </button>
              <label className="icm-btn icm-btn-secondary icm-upload-btn">
                📄 Upload CSV
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleCSVUpload}
                  hidden
                />
              </label>
              <span className="icm-hint">
                CSV format: <code>name,email</code>
              </span>
            </div>

            <div className="icm-rows">
              <div className="icm-row icm-row-head">
                <span>#</span>
                <span>Name (optional)</span>
                <span>Email *</span>
                <span />
              </div>

              {rows.map((row, i) => (
                <div className="icm-row" key={i}>
                  <span className="icm-row-num">{i + 1}</span>
                  <input
                    className="icm-input"
                    placeholder="e.g. Aditi Sharma"
                    value={row.name}
                    onChange={(e) => updateRow(i, 'name', e.target.value)}
                  />
                  <input
                    className="icm-input"
                    type="email"
                    placeholder="student@example.com"
                    value={row.email}
                    onChange={(e) => updateRow(i, 'email', e.target.value)}
                  />
                  <button
                    className="icm-row-remove"
                    onClick={() => removeRow(i)}
                    disabled={rows.length === 1}
                    title="Remove row"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {results && (
          <div className="icm-results">
            <div className="icm-result-block icm-result-success">
              <h4>✅ Sent ({results.sent.length})</h4>
              <ul>
                {results.sent.map((r) => (
                  <li key={r.email}>{r.email}</li>
                ))}
                {results.sent.length === 0 && <li className="icm-muted">—</li>}
              </ul>
            </div>
            {results.failed.length > 0 && (
              <div className="icm-result-block icm-result-failed">
                <h4>⚠️ Failed ({results.failed.length})</h4>
                <ul>
                  {results.failed.map((r) => (
                    <li key={r.email}>
                      <strong>{r.email}</strong>
                      {r.error ? ` — ${r.error}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <footer className="icm-footer">
          {!results ? (
            <>
              <button
                className="icm-btn icm-btn-ghost"
                onClick={closeAfterSuccess}
                disabled={sending}
              >
                Cancel
              </button>
              <button
                className="icm-btn icm-btn-primary"
                onClick={handleSend}
                disabled={sending}
              >
                {sending ? '⏳ Sending…' : '✉️ Send Invitations'}
              </button>
            </>
          ) : (
            <button
              className="icm-btn icm-btn-primary"
              onClick={closeAfterSuccess}
            >
              Done
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}